import { useState, useEffect, useRef } from "react";
import { useLanguage } from "./LanguageContext";

// API base — in production this points to the same domain.
// In development you can set REACT_APP_API_URL=http://localhost:8000 in .env
const API_URL = (process.env.REACT_APP_API_URL || "") + "/api/gallery.php";
const UPLOADS_URL = (process.env.REACT_APP_API_URL || "") + "/uploads/gallery/";

export default function Gallery() {
  const { t } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();

  // Fetch photos on mount
  useEffect(() => {
    fetch(API_URL + "?action=list")
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    const formData = new FormData();
    files.forEach((file) => formData.append("photos[]", file));

    fetch(API_URL + "?action=upload", {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data.photos);
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      })
      .catch(() => setUploading(false));
  };

  const handleDelete = (filename) => {
    fetch(API_URL + "?action=delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data.photos);
        setLightbox(null);
      });
  };

  const handleDownload = (photo) => {
    const url = UPLOADS_URL + photo.filename;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      // iOS can't use download attr — open in new tab for long-press save
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <section id="gallery" style={{ padding: "80px 20px", background: "white" }}>
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <p style={{
          letterSpacing: "3px",
          textTransform: "uppercase",
          fontSize: "0.85rem",
          color: "var(--blue-400)",
          marginBottom: "8px",
        }}>
          {t.gallerySubtitle}
        </p>
        <h2 className="section-heading">{t.galleryHeading}</h2>
        <div className="divider-ornament">
          <span style={{ color: "var(--gold)" }}></span>
        </div>
        <p style={{
          color: "#64748b",
          maxWidth: "500px",
          margin: "16px auto 0",
          fontSize: "1rem",
        }}>
          {t.galleryDescription}
        </p>
      </div>

      {/* Upload button */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleUpload}
          style={{ display: "none" }}
          id="gallery-upload"
        />
        <button
          className="btn-wedding"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            opacity: uploading ? 0.7 : 1,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {uploading ? t.galleryUploading : t.galleryUpload}
        </button>
      </div>

      {/* Photo grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{
            width: "40px",
            height: "40px",
            border: "3px solid var(--blue-200)",
            borderTop: "3px solid var(--blue-600)",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : photos.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "60px 20px",
          maxWidth: "500px",
          margin: "0 auto",
        }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--blue-300)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 16px" }}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.1rem",
            color: "var(--blue-400)",
            fontStyle: "italic",
          }}>
            {t.galleryEmpty}
          </p>
        </div>
      ) : (
        <div style={{
          maxWidth: "1000px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: "16px",
        }}>
          {photos.map((photo) => (
            <div
              key={photo.filename}
              style={{
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                cursor: "pointer",
                aspectRatio: "1",
                background: "#f1f5f9",
                transition: "transform 0.3s, box-shadow 0.3s",
              }}
              onClick={() => setLightbox(photo)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
              }}
            >
              <img
                src={UPLOADS_URL + photo.filename}
                alt={photo.filename}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setLightbox(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setLightbox(null)}
              style={{
                position: "absolute",
                top: "-40px",
                right: "0",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "2rem",
                cursor: "pointer",
                lineHeight: 1,
              }}
              aria-label="Close"
            >
              x
            </button>

            <img
              src={UPLOADS_URL + lightbox.filename}
              alt={lightbox.filename}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                borderRadius: "8px",
                objectFit: "contain",
              }}
            />

            {/* Action buttons */}
            <div style={{
              display: "flex",
              gap: "12px",
              marginTop: "16px",
            }}>
              <button
                onClick={() => handleDownload(lightbox)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 24px",
                  background: "var(--blue-600)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: "'Lato', sans-serif",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  transition: "background 0.3s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--blue-700)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--blue-600)")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t.galleryDownload}
              </button>

              <button
                onClick={() => handleDelete(lightbox.filename)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 24px",
                  background: "transparent",
                  color: "#ef4444",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: "'Lato', sans-serif",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ef4444";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#ef4444";
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                {t.galleryDelete}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
